/**
 * AI CONTEXT
 * Purpose: composes class table rows with curriculum + teacher joins.
 * Data flow: filtered classes -> curriculum/user lookups -> Class mapper -> sort -> optimized search.
 * Uses: semester/search/sort settings and fulfillment checks.
 * Used by: ClassesTable.
 * Refactor: avoid potential false-ready condition when classes array is empty-but-valid.
 */
import { keyBy, orderBy, uniq } from "lodash"
import { useMemo } from "react"

import { classAtoms, curriculumAtoms } from "#atoms"
import { Class } from "#classes/Class"
import {
  CLASS_SEARCHABLE_FIELDS,
  CLASS_SORTABLE_FIELDS,
  getClassSortValue,
} from "#constants/classOptions"
import { USER_SETTINGS } from "#constants/globalConstants"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  type ClassConstructor,
  ClassForTable,
} from "#customTypes/composedTypes/classTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { SETTINGS_KEYS } from "#customTypes/globalTypes"
import { useIsFulfilled } from "#hooks/jotai/useCascadingData"
import { useFilteredDocs, useSomeDocs } from "#hooks/jotai/useSubscription"
import { useGetSomeUsers } from "#hooks/newHooks/useGetUser"
import useOptimizedSearch from "#hooks/newHooks/useOptimizedSearch"

import { useTableSort } from "./useTableSort"

const useClassTableData = () => {
  const { getUserSetting } = useUserContext()
  const filterSemester = getUserSetting<string>(
    USER_SETTINGS.CLASSES_TABLE_SEMESTER
  )
  const resultsCount = getUserSetting<number>(
    USER_SETTINGS.CLASSES_TABLE_RESULTS_COUNT
  )
  const defaultSearchTerm = getUserSetting<string>(
    USER_SETTINGS.CLASSES_TABLE_SEARCH_TERM
  )

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<ClassForTable>({
      tableKey: SETTINGS_KEYS.CLASSES,
      sortableFields: CLASS_SORTABLE_FIELDS,
      getSortValue: getClassSortValue,
    })

  const query: FirebaseQueryConfig = useMemo(() => {
    if (filterSemester === "All") return {}

    return {
      queries: {
        semester: { comparator: COMPARATORS.EQUALS, value: filterSemester },
      },
    }
  }, [filterSemester])

  const classesFromDb = useFilteredDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    query
  )

  const curriculumIds = useMemo(
    () => uniq(classesFromDb?.map((classObj) => classObj.curriculumId)).sort(),
    [classesFromDb]
  )

  const curriculumsFromDb = useSomeDocs<CurriculumConstructor>(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    curriculumIds
  )

  const curriculumsById = useMemo(() => {
    return keyBy(curriculumsFromDb, "id")
  }, [curriculumsFromDb])

  const teacherIds = useMemo(
    () =>
      uniq(classesFromDb?.map((classObj) => classObj.teacherIds ?? []).flat()),
    [classesFromDb]
  )

  const teachersFromDb = useGetSomeUsers(teacherIds)

  const teachersById = useMemo(() => {
    return keyBy(teachersFromDb, "id")
  }, [teachersFromDb])

  // Track cascading data fulfillment with 5s timeout
  const curriculumsFulfilled = useIsFulfilled(
    curriculumIds,
    curriculumsFromDb,
    3000
  )

  const allFulfilled = classesFromDb.length > 0 && curriculumsFulfilled

  const baseRows = useMemo(() => {
    if (!allFulfilled) return []

    return orderBy(classesFromDb, "lastUpdatedAt", "desc").map((classObj) => {
      const curriculum = curriculumsById[classObj.curriculumId] ?? null
      const teachers = classObj.teacherIds
        ?.map((teacherId) => teachersById[teacherId])
        .filter(Boolean)

      return {
        ...new Class(classObj).getClassForTable(curriculum, teachers),
        lastUpdatedAt: classObj.lastUpdatedAt,
      }
    })
  }, [classesFromDb, curriculumsById, teachersById, allFulfilled])

  const sortedRows = useMemo(() => sortRows(baseRows), [sortRows, baseRows])

  const { filteredItems: filteredClasses, onSearch } =
    useOptimizedSearch<ClassForTable>(
      sortedRows,
      CLASS_SEARCHABLE_FIELDS,
      resultsCount,
      defaultSearchTerm
    )

  const classesForTable = useMemo(
    () => sortRows(filteredClasses),
    [sortRows, filteredClasses]
  )

  return {
    classesForTable,
    isReady: allFulfilled,
    // errors,
    onSearch,
    sortField,
    sortOrder,
    onSortChange,
  }
}

export default useClassTableData
